/**
 * Institution Form Modal
 *
 * Modal para crear/editar instituciones.
 * Al crear, muestra las credenciales del admin generadas en un paso separado.
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CheckCircle2, Copy, Eye, EyeOff, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Input } from '@/presentation/components/ui/Input'
import { Button } from '@/presentation/components/ui/Button'
import { useCreateInstitution, useUpdateInstitution } from '@/application/institutions'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type { Institution } from '@/domain/institution/types'
import type { CreateInstitutionResponse } from '@/domain/institution/types'

// =============================================================================
// Schema
// =============================================================================

const createInstitutionSchema = z.object({
  name: z.string().min(3, 'El nombre debe tener al menos 3 caracteres'),
  slug: z
    .string()
    .min(3, 'El slug debe tener al menos 3 caracteres')
    .regex(/^[a-z0-9-]+$/, 'Solo letras minúsculas, números y guiones'),
  nit: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  adminEmail: z.string().email('Email del administrador inválido'),
})

const updateInstitutionSchema = createInstitutionSchema.omit({
  slug: true,
  adminEmail: true,
})

type CreateFormData = z.infer<typeof createInstitutionSchema>
type UpdateFormData = z.infer<typeof updateInstitutionSchema>

// =============================================================================
// Credentials Step
// =============================================================================

function CredentialsStep({
  result,
  onClose,
}: {
  result: CreateInstitutionResponse
  onClose: () => void
}) {
  const [showPassword, setShowPassword] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  return (
    <div className="space-y-4">
      {/* Success banner — usa colores semánticos del sistema */}
      <div
        className="flex items-start gap-3 rounded-lg p-4"
        style={{
          background: 'color-mix(in srgb, var(--color-success) 10%, var(--background-secondary))',
          border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)',
        }}
      >
        <CheckCircle2
          className="h-5 w-5 mt-0.5 flex-shrink-0"
          style={{ color: 'var(--color-success)' }}
        />
        <div>
          <p className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>
            Institución creada correctamente
          </p>
          <p className="text-sm mt-0.5" style={{ color: 'var(--foreground-secondary)' }}>
            Se generaron las credenciales para el administrador de{' '}
            <strong>{result.institution.name}</strong>.
          </p>
        </div>
      </div>

      {/* Credentials */}
      <div className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--foreground-muted)' }}>
          Credenciales del administrador
        </p>

        {/* Email */}
        <div className="space-y-1.5">
          <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>Email</p>
          <div
            className="flex items-center gap-2 rounded-lg px-3 py-2.5"
            style={{ background: 'var(--background-secondary)', border: '1px solid var(--border)' }}
          >
            <span className="flex-1 text-sm font-mono select-all" style={{ color: 'var(--foreground)' }}>
              {result.adminUser.email}
            </span>
            <button
              type="button"
              onClick={() => copy(result.adminUser.email, 'email')}
              className="transition-colors"
              style={{ color: 'var(--foreground-muted)' }}
              title="Copiar"
            >
              {copiedField === 'email' ? (
                <CheckCircle2 className="h-4 w-4" style={{ color: 'var(--color-success)' }} />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>Contraseña temporal</p>
          <div
            className="flex items-center gap-2 rounded-lg px-3 py-2.5"
            style={{ background: 'var(--background-secondary)', border: '1px solid var(--border)' }}
          >
            <span className="flex-1 text-sm font-mono select-all tracking-wider" style={{ color: 'var(--foreground)' }}>
              {showPassword ? result.adminUser.temporaryPassword : '••••••••••••••••'}
            </span>
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="transition-colors"
              style={{ color: 'var(--foreground-muted)' }}
              title={showPassword ? 'Ocultar' : 'Mostrar'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => copy(result.adminUser.temporaryPassword, 'password')}
              className="transition-colors"
              style={{ color: 'var(--foreground-muted)' }}
              title="Copiar"
            >
              {copiedField === 'password' ? (
                <CheckCircle2 className="h-4 w-4" style={{ color: 'var(--color-success)' }} />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Warning — usa color semántico warning del sistema */}
      <div
        className="flex items-start gap-2.5 rounded-lg p-3"
        style={{
          background: 'color-mix(in srgb, var(--color-warning) 10%, var(--background-secondary))',
          border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
        }}
      >
        <AlertTriangle
          className="h-4 w-4 mt-0.5 flex-shrink-0"
          style={{ color: 'var(--color-warning)' }}
        />
        <p className="text-xs leading-relaxed" style={{ color: 'var(--foreground-secondary)' }}>
          Esta contraseña solo se muestra una vez. El administrador deberá cambiarla en
          su primer inicio de sesión. Compártela de forma segura.
        </p>
      </div>

      <DialogFooter>
        <Button onClick={onClose}>Listo</Button>
      </DialogFooter>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

interface InstitutionFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  institution?: Institution | null
}

export function InstitutionFormModal({
  open,
  onOpenChange,
  institution,
}: InstitutionFormModalProps) {
  const isEditing = !!institution
  const createMutation = useCreateInstitution()
  const updateMutation = useUpdateInstitution()
  const [createdResult, setCreatedResult] = useState<CreateInstitutionResponse | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(isEditing ? updateInstitutionSchema : createInstitutionSchema),
    defaultValues: {
      name: '',
      slug: '',
      nit: '',
      address: '',
      city: '',
      phone: '',
      email: '',
      adminEmail: '',
    },
  })

  useEffect(() => {
    if (open) {
      setCreatedResult(null)
      if (institution) {
        reset({
          name: institution.name,
          nit: institution.nit || '',
          address: institution.address || '',
          city: institution.city || '',
          phone: institution.phone || '',
          email: institution.email || '',
        })
      } else {
        reset({ name: '', slug: '', nit: '', address: '', city: '', phone: '', email: '', adminEmail: '' })
      }
    }
  }, [open, institution, reset])

  // Auto-generate slug from name (solo al crear)
  const watchName = watch('name')
  useEffect(() => {
    if (!isEditing && watchName) {
      const slug = watchName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      setValue('slug', slug)
    }
  }, [watchName, isEditing, setValue])

  const onSubmit = async (data: CreateFormData | UpdateFormData) => {
    if (isEditing) {
      updateMutation.mutate(
        { id: institution.id, data: data as UpdateFormData },
        {
          onSuccess: () => {
            toast.success('Institución actualizada correctamente')
            onOpenChange(false)
          },
          onError: (err) => toast.error(getErrorMessage(err, 'Error al actualizar la institución')),
        }
      )
    } else {
      createMutation.mutate(data as CreateFormData, {
        onSuccess: (result) => setCreatedResult(result),
        onError: (err) => toast.error(getErrorMessage(err, 'Error al crear la institución')),
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setCreatedResult(null); onOpenChange(false) } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {createdResult ? 'Credenciales de acceso' : isEditing ? 'Editar Institución' : 'Nueva Institución'}
          </DialogTitle>
          {!createdResult && (
            <DialogDescription>
              {isEditing
                ? 'Modifica los datos de la institución'
                : 'Ingresa los datos para crear una nueva institución. Se generará un usuario administrador automáticamente.'}
            </DialogDescription>
          )}
        </DialogHeader>

        {createdResult ? (
          <CredentialsStep result={createdResult} onClose={() => { setCreatedResult(null); onOpenChange(false) }} />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <Input
              label="Nombre *"
              placeholder="Hospital San José"
              error={errors.name?.message}
              {...register('name')}
            />

            {!isEditing && (
              <Input
                label="Slug *"
                placeholder="hospital-san-jose"
                helperText="Identificador único (se genera automáticamente)"
                error={(errors as { slug?: { message?: string } }).slug?.message}
                {...register('slug')}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="NIT"
                placeholder="900123456-1"
                error={errors.nit?.message}
                {...register('nit')}
              />
              <Input
                label="Ciudad"
                placeholder="Bogotá"
                error={errors.city?.message}
                {...register('city')}
              />
            </div>

            <Input
              label="Dirección"
              placeholder="Calle 100 # 10-20"
              error={errors.address?.message}
              {...register('address')}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Teléfono"
                placeholder="+57 1 234 5678"
                error={errors.phone?.message}
                {...register('phone')}
              />
              <Input
                label="Email institucional"
                type="email"
                placeholder="contacto@hospital.com"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>

            {!isEditing && (
              <Input
                label="Email del Administrador *"
                type="email"
                placeholder="admin@hospital.com"
                helperText="Se creará un usuario administrador con este email y una contraseña temporal"
                error={(errors as { adminEmail?: { message?: string } }).adminEmail?.message}
                {...register('adminEmail')}
              />
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Creando...' : isEditing ? 'Guardar cambios' : 'Crear institución'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
